import { Entity } from "./Entity.js";
import { Projectile } from "./Projectile.js";
import { GRAVITY } from "../constants.js";
import { COMBAT_TUNING } from "../data/tuning.js";

function scaleDamping(basePerFrame, dtScale) {
  return Math.pow(basePerFrame, dtScale);
}

export class Enemy extends Entity {
  constructor(scene, x, y, typeDef) {
    super(scene, x, y - typeDef.h, typeDef.w, typeDef.h, typeDef.hp);
    this.setHurtboxProfile(typeDef.behavior === "melee" && typeDef.h >= 56 ? "enemy_melee" : "enemy_light");
    this.typeDef = typeDef;
    this.prefix = typeDef.prefix;
    this.spawnX = x;
    this.spawnY = y - typeDef.h;
    this.sprite = null;
    this.hpBar = null;
    this.hpBarVisible = false;
    this.respawnTimer = 0;

    // AI state
    this.aiState = "idle";     // idle, patrol, chase, attack
    this.idleTimer = 0;
    this.idleDuration = 1000 + Math.random() * 1000;
    this.patrolDir = Math.random() < 0.5 ? 1 : -1;
    this.onGround = false;

    // Attack state
    this.attacking = false;
    this.attackTime = 0;
    this.attackHitDealt = false;
    this.attackCooldown = 0;
    this.projectileSpawned = false;
    this.attackLockTimer = 0;
  }

  lockAttacks(durationMs) {
    if (!durationMs || durationMs <= 0) return;
    this.attackLockTimer = Math.max(this.attackLockTimer, durationMs);
    this.attacking = false;
    this.attackTime = 0;
    this.attackHitDealt = false;
    this.projectileSpawned = false;
  }

  animKey(suffix) {
    return this.prefix + "_" + suffix;
  }

  create() {
    this.sprite = this.scene.add.sprite(0, 0, this.animKey("idle"))
      .setOrigin(this.typeDef.originX, this.typeDef.originY)
      .setDepth(1000);
    this.sprite.play(this.animKey("idle"));
    this.hpBar = this.scene.add.graphics().setDepth(6);
    this.syncSprite();
    this.drawHpBar();
  }

  syncSprite() {
    const targetCenterX = this.x + this.w / 2;
    this.sprite.x = targetCenterX;
    this.sprite.y = this.y + this.h;
    this.sprite.setFlipX(this.facing === -1);

    // Keep visual center pinned to entity center when flipping asymmetric frames.
    const b = this.sprite.getBounds();
    this.sprite.x += targetCenterX - b.centerX;
    this.sprite.setDepth(1000 + this.sprite.y);
  }

  drawHpBar() {
    this.hpBar.clear();
    if (this.dead || this.dying || !this.hpBarVisible) return;
    const barW = 30;
    const barH = 4;
    const barX = this.x + (this.w - barW) / 2;
    const barY = this.y - 50;
    const fillW = (this.hp / this.maxHp) * (barW - 2);
    // Keep enemy HP bar above the enemy and foreground gameplay visuals.
    this.hpBar.setDepth(1002 + this.y + this.h);
    this.hpBar.fillStyle(0x000000, 0.5);
    this.hpBar.fillRect(barX, barY, barW, barH);
    this.hpBar.fillStyle(this.hp > this.maxHp * 0.15 ? 0x4caf50 : 0xf44336, 1);
    this.hpBar.fillRect(barX + 1, barY + 1, fillW, barH - 2);
  }

  takeDamage(amount, knockbackDir, combatMode = "pve") {
    const prevHp = this.hp;
    const killed = super.takeDamage(amount, knockbackDir, combatMode);
    if (this.hp === prevHp) return false;
    this.hpBarVisible = true;
    if (killed) {
      this.sprite.play(this.animKey("dying"));
      this.attacking = false;
    } else {
      this.sprite.play(this.animKey("hurt"));
      this.attacking = false;
      this.aiState = "idle";
      this.idleTimer = 0;
      this.idleDuration = 300;
    }
    this.drawHpBar();
    return killed;
  }

  getPlayerDist() {
    const p = this.scene.playerEntity;
    const myCx = this.x + this.w / 2;
    const pCx = p.x + p.w / 2;
    // Edge-to-edge gap (negative means overlapping)
    const gap = Math.abs(pCx - myCx) - this.w / 2 - p.w / 2;
    return { dx: pCx - myCx, dist: Math.abs(pCx - myCx), gap, player: p };
  }

  hasGroundAhead(dir) {
    const checkX = dir > 0 ? this.x + this.w + 4 : this.x - 4;
    const feetY = this.y + this.h;
    const result = this.scene.findGroundY(checkX, feetY - 4, feetY + 20, null);
    return result !== null;
  }

  getActiveHitbox() {
    if (!this.attacking || this.typeDef.behavior === "ranged") return null;
    const def = this.typeDef;
    const progress = this.attackTime / def.attackDuration;
    if (progress < 0.25 || progress > 0.625) return null;
    const hbW = 40;
    const hbH = this.h;
    const offsetX = this.facing > 0 ? this.w : -hbW;
    return {
      x: this.x + offsetX,
      y: this.y,
      w: hbW,
      h: hbH,
      damage: def.attackDamage,
    };
  }

  updateAI(dt) {
    if (this.attackLockTimer > 0) {
      this.attackLockTimer = Math.max(0, this.attackLockTimer - dt);
    }
    if (this.hitStopTimer > 0 || this.hurting || this.dying || this.dead) {
      this.vx = 0;
      return;
    }

    if (this.attackCooldown > 0) this.attackCooldown -= dt;

    const { dx, dist, gap, player } = this.getPlayerDist();
    const def = this.typeDef;
    const isRanged = def.behavior === "ranged";
    const canThrow = !!def.projectile;
    const throwRange = Number.isFinite(def.throwRange) ? def.throwRange : def.attackRange;

    // Attack update (if in progress)
    if (this.attacking) {
      this.attackTime += dt;
      this.vx = 0;

      // Spawn projectile mid-attack when this enemy has a throwable configured.
      if (canThrow && !this.projectileSpawned) {
        const progress = this.attackTime / def.attackDuration;
        if (progress >= 0.5) {
          this.projectileSpawned = true;
          const cx = this.x + this.w / 2 + this.facing * 20;
          const cy = this.y + this.h * 0.4;
          const proj = new Projectile(
            this.scene, cx, cy, this.facing,
            def.attackDamage, def.projectile, this
          );
          proj.create();
          this.scene.entities.push(proj);
        }
      }

      if (this.attackTime >= def.attackDuration) {
        this.attacking = false;
        const defaultCooldown = isRanged ? COMBAT_TUNING.enemyAttackCooldown.ranged : COMBAT_TUNING.enemyAttackCooldown.melee;
        const cooldownBase = Number.isFinite(def.attackCooldownBase) ? def.attackCooldownBase : defaultCooldown.base;
        const cooldownJitter = Number.isFinite(def.attackCooldownJitter) ? def.attackCooldownJitter : defaultCooldown.jitter;
        this.attackCooldown = cooldownBase + Math.random() * Math.max(0, cooldownJitter);
        this.aiState = "idle";
        this.idleTimer = 0;
        this.idleDuration = isRanged ? 400 : 200;
      }
      return;
    }

    switch (this.aiState) {
      case "idle":
        this.vx = 0;
        this.idleTimer += dt;
        // Check for aggro
        if (dist <= def.aggroRange && !player.dead) {
          this.aiState = "chase";
          break;
        }
        if (this.idleTimer >= this.idleDuration) {
          this.aiState = "patrol";
        }
        break;

      case "patrol":
        // Check for aggro
        if (dist <= def.aggroRange && !player.dead) {
          this.aiState = "chase";
          break;
        }
        // Walk in patrol direction
        this.vx = def.speed * this.patrolDir;
        this.facing = this.patrolDir;

        // Turn around at edges or patrol distance limit
        const distFromSpawn = this.x + this.w / 2 - this.spawnX;
        if (Math.abs(distFromSpawn) > def.patrolDistance ||
            (this.onGround && !this.hasGroundAhead(this.patrolDir))) {
          this.patrolDir *= -1;
          this.vx = 0;
          this.aiState = "idle";
          this.idleTimer = 0;
          this.idleDuration = 500 + Math.random() * 500;
        }
        break;

      case "chase": {
        // Lost interest (leash)
        if (dist > def.aggroRange * 1.5 || player.dead) {
          this.aiState = "patrol";
          this.patrolDir = this.facing;
          break;
        }

        // Ranged: retreat if player gets too close
        if (isRanged && def.retreatRange && dist < def.retreatRange) {
          this.facing = dx > 0 ? 1 : -1;
          const retreatDir = -this.facing;
          if (this.onGround && !this.hasGroundAhead(retreatDir)) {
            this.vx = 0;
          } else {
            this.vx = def.speed * retreatDir;
          }
          break;
        }

        // Close enough to attack (use gap for melee, dist for ranged)
        const inMeleeRange = gap <= def.attackRange;
        const inThrowRange = canThrow && dist <= throwRange;
        const inRange = isRanged ? dist <= def.attackRange : (inMeleeRange || inThrowRange);
        if (inRange && this.attackCooldown <= 0 && this.attackLockTimer <= 0) {
          this.vx = 0;
          this.facing = dx > 0 ? 1 : -1;
          this.aiState = "attack";
          this.attacking = true;
          this.attackTime = 0;
          this.attackHitDealt = false;
          this.projectileSpawned = false;
          const attackAnim = def.attackAnim || "slashing";
          this.sprite.play(this.animKey(attackAnim));
          break;
        }

        // Face the player
        this.facing = dx > 0 ? 1 : -1;

        // Don't walk into the player — stop when close enough
        const minGap = 8;
        if (!isRanged && gap <= minGap) {
          this.vx = 0;
        } else if (isRanged && dist <= def.attackRange) {
          this.vx = 0;
        } else if (this.onGround && !this.hasGroundAhead(this.facing)) {
          this.vx = 0;
        } else {
          const speed = isRanged ? def.speed : def.chaseSpeed;
          this.vx = speed * this.facing;
        }
        break;
      }

      case "attack":
        // Handled by attacking block above
        break;
    }
  }

  resolveAnimState() {
    if (this.attacking || this.hurting || this.dying || this.dead) return;

    const moving = Math.abs(this.vx) > 0.1;
    const isChasing = this.aiState === "chase";
    let targetAnim;
    if (moving) {
      targetAnim = isChasing ? this.animKey("running") : this.animKey("walking");
    } else {
      targetAnim = this.animKey("idle");
    }

    if (this.sprite.anims.currentAnim?.key !== targetAnim) {
      this.sprite.play(targetAnim);
    }
  }

  update(dt) {
    super.update(dt);
    const dtScale = this.scene.frameScale || 1;

    // Gravity
    if (!this.onGround) {
      this.vy += GRAVITY * dtScale;
    }

    // Horizontal-only knockback. Avoid vertical snapping artifacts on uneven ground.
    if (this.hitStopTimer <= 0 && this.knockbackVx !== 0) {
      this.x += this.knockbackVx * dtScale;
      this.knockbackVx *= scaleDamping(COMBAT_TUNING.knockbackDecayX, dtScale);
      if (Math.abs(this.knockbackVx) < 0.1) this.knockbackVx = 0;
      this.knockbackVy = 0;
    }

    // AI
    this.updateAI(dt);

    if (this.dying) {
      this.sprite.setAlpha(Math.max(0, 1 - this.dieTime / 500));
      if (this.dead) {
        this.dying = false;
        this.respawnTimer = 3000;
        this.sprite.setVisible(false);
        this.hpBar.clear();
      }
    } else if (this.dead) {
      this.respawnTimer -= dt;
      if (this.respawnTimer <= 0) {
        this.x = this.spawnX;
        this.y = this.spawnY;
        this.hp = this.maxHp;
        this.dead = false;
        this.dying = false;
        this.dieTime = 0;
        this.hurting = false;
        this.hurtTime = 0;
        this.hurtGraceTimer = 0;
        this.lastCombatMode = "pve";
        this.knockbackVx = 0;
        this.knockbackVy = 0;
        this.vx = 0;
        this.vy = 0;
        this.attacking = false;
        this.attackCooldown = 0;
        this.attackLockTimer = 0;
        this.projectileSpawned = false;
        this.aiState = "idle";
        this.idleTimer = 0;
        this.idleDuration = 1000 + Math.random() * 1000;
        this.hpBarVisible = false;
        this.sprite.setVisible(true);
        this.sprite.setAlpha(1);
        this.sprite.clearTint();
        this.sprite.play(this.animKey("idle"));
        this.drawHpBar();
      }
    } else if (this.hurting) {
      // Keep hurt animation without tint flash.
      this.sprite.clearTint();
    } else {
      this.sprite.clearTint();
      this.resolveAnimState();
    }

    this.syncSprite();
    this.drawHpBar();
  }
}
